import React, { useState } from 'react'
import type { FilterField } from '@/types/Dashboards.types'

interface FilterConfigCardProps {
  filters: FilterField[]
  onConfirm: (filters: FilterField[]) => void
  onCustomize: () => void
  locked: boolean
}

/**
 * FilterConfigCard — displays pre-filled SDK filters for user confirmation
 * during the AI dashboard creation flow (Step 3b).
 *
 * Renders each filter by type:
 * - select: interactive dropdown (or read-only label when locked), no Edit button
 * - checkbox_group: inline checkboxes with local toggle state; shows placeholder when no options available
 * - text: display value + Edit button
 *
 * Both action buttons disable when locked. Edit button calls onCustomize().
 */
export const FilterConfigCard: React.FC<FilterConfigCardProps> = ({
  filters,
  onConfirm,
  onCustomize,
  locked,
}) => {
  // Local state mirrors filter values so checkbox_group and select rows are interactive before confirming
  const [localFilters, setLocalFilters] = useState<FilterField[]>(() =>
    filters.map(f => ({ ...f }))
  )

  const handleCheckboxToggle = (filterId: string, option: string, checked: boolean) => {
    setLocalFilters(prev =>
      prev.map(f => {
        if (f.filterId !== filterId) return f
        const currentValues = Array.isArray(f.value) ? f.value : []
        const nextValues = checked
          ? [...currentValues, option]
          : currentValues.filter(v => v !== option)
        return { ...f, value: nextValues }
      })
    )
  }

  const handleSelectChange = (filterId: string, value: string) => {
    setLocalFilters(prev =>
      prev.map(f => f.filterId !== filterId ? f : { ...f, value })
    )
  }

  const handleConfirm = () => {
    onConfirm(localFilters)
  }

  return (
    <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-[10px] overflow-hidden mb-[14px]">
      {/* Filter rows */}
      {localFilters.map(filter => (
        <div
          key={filter.filterId}
          className="px-[14px] py-[10px] flex items-start justify-between border-b border-[#e2e8f0] last:border-b-0"
        >
          <div className="flex-1 min-w-0">
            {/* Label row */}
            <div className="text-[11px] font-semibold text-secondary uppercase tracking-[0.06em] mb-[4px]">
              {filter.label}
              {filter.required && (
                <span className="text-destructive text-[10px] font-semibold ml-1">Required</span>
              )}
            </div>

            {/* Value rendering */}
            {filter.type === 'checkbox_group' ? (
              filter.options && filter.options.length > 0 ? (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {filter.options.map(option => {
                    const currentValues = Array.isArray(filter.value) ? filter.value : []
                    const isChecked = currentValues.includes(option.value)
                    return (
                      <label
                        key={option.value}
                        className={`flex items-center gap-1.5 cursor-pointer ${locked ? 'pointer-events-none' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="w-[13px] h-[13px] accent-accent"
                          checked={isChecked}
                          disabled={locked}
                          onChange={e => handleCheckboxToggle(filter.filterId, option.value, e.target.checked)}
                        />
                        <span
                          className={`text-[12px] ${isChecked ? 'font-medium text-primary' : 'text-secondary'}`}
                        >
                          {option.label}
                        </span>
                      </label>
                    )
                  })}
                </div>
              ) : (
                // No options available (e.g. session requires credential + season to load)
                <p className="text-[11px] text-muted-foreground italic mt-[2px]">
                  {filter.required ? '(required — customize to select)' : '(optional — leave blank to get all)'}
                </p>
              )
            ) : filter.type === 'select' && filter.options && filter.options.length > 0 ? (
              locked ? (
                // Locked: show the label of the selected option (or raw value as fallback)
                <p className="text-[13px] text-primary font-medium">
                  {filter.options.find(o => o.value === String(filter.value))?.label ?? String(filter.value)}
                </p>
              ) : (
                // Unlocked: interactive dropdown
                <select
                  aria-label={filter.label}
                  className="mt-[2px] text-[13px] text-primary font-medium bg-white border border-[#e2e8f0] rounded-[6px] px-[8px] py-[4px] cursor-pointer focus:outline-none focus:ring-1 focus:ring-accent"
                  value={String(filter.value)}
                  onChange={e => handleSelectChange(filter.filterId, e.target.value)}
                >
                  {filter.options.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )
            ) : (
              <>
                {filter.value && String(filter.value).trim() !== '' ? (
                  <p className="text-[13px] text-primary font-medium">{String(filter.value)}</p>
                ) : !filter.required ? (
                  <p className="text-[11px] text-muted-foreground italic mt-[2px]">
                    (optional — leave blank to get all)
                  </p>
                ) : null}
              </>
            )}
          </div>

          {/* Edit button — only for text type (not select or checkbox_group) */}
          {filter.type !== 'checkbox_group' && filter.type !== 'select' && (
            <button
              type="button"
              aria-label={`Edit ${filter.label} filter`}
              className={`text-[12px] text-accent font-medium bg-transparent border-0 cursor-pointer underline whitespace-nowrap mt-[2px] ml-3 ${locked ? 'pointer-events-none opacity-50' : ''}`}
              onClick={onCustomize}
              disabled={locked}
            >
              Edit
            </button>
          )}
        </div>
      ))}

      {/* Action buttons */}
      <div className="px-[14px] py-[10px]">
        <button
          type="button"
          className={`w-full bg-accent text-white rounded-[8px] py-[10px] text-[13px] font-semibold mb-2 ${locked ? 'opacity-65 cursor-default pointer-events-none' : ''}`}
          onClick={handleConfirm}
          disabled={locked}
        >
          These look right → Continue
        </button>
        <button
          type="button"
          className={`w-full bg-card text-primary border border-[#e2e8f0] rounded-[8px] py-[10px] text-[13px] font-medium ${locked ? 'opacity-60 cursor-default pointer-events-none' : ''}`}
          onClick={onCustomize}
          disabled={locked}
        >
          Customize filters
        </button>
      </div>
    </div>
  )
}
